import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

/**
 * Generic confirmation modal component
 * Reusable modal for confirming user actions throughout the application
 * Returns true if user confirms, false if user cancels
 */
@Component({
	selector: 'app-confirm-modal',
	standalone: true,
	imports: [CommonModule],
	template: `
		<div class="modal-header">
			<h4 class="modal-title">
				<i
					*ngIf="showIcon"
					[class]="icon + ' me-2'"
					[ngClass]="{
						'text-warning': confirmButtonClass === 'btn-warning',
						'text-danger': confirmButtonClass === 'btn-danger',
					}"
				></i>
				{{ title }}
			</h4>
			<button
				type="button"
				class="btn-close"
				aria-label="Close"
				[disabled]="loading"
				(click)="cancel()"
			></button>
		</div>

		<div class="modal-body">
			<p class="mb-0">{{ message }}</p>
		</div>

		<div class="modal-footer">
			<button
				type="button"
				class="btn btn-secondary"
				[disabled]="loading"
				(click)="cancel()"
			>
				<!-- <i class="bi bi-x-lg me-1"></i> -->
				{{ cancelButtonText }}
			</button>
			<button
				type="button"
				class="btn"
				[ngClass]="confirmButtonClass"
				[disabled]="loading"
				(click)="confirm()"
			>
				<!-- <i *ngIf="!loading" class="bi bi-check-lg me-1"></i> -->
				<span
					*ngIf="loading"
					class="spinner-border spinner-border-sm me-1"
					role="status"
					aria-hidden="true"
				></span>
				{{ confirmButtonText }}
			</button>
		</div>
	`,
	styles: [
		`
			.modal-footer .btn {
				min-width: 120px;
			}
		`,
	],
})
export class ConfirmModalComponent {
	/**
	 * Modal title text
	 */
	@Input() title = 'Vahvista toiminto';

	/**
	 * Confirmation message to display
	 */
	@Input() message = '';

	/**
	 * Text for the confirm button
	 */
	@Input() confirmButtonText = 'Vahvista';

	/**
	 * Text for the cancel button
	 */
	@Input() cancelButtonText = 'Peruuta';

	/**
	 * Bootstrap button class for confirm button (btn-danger, btn-warning, btn-primary, etc.)
	 */
	@Input() confirmButtonClass = 'btn-danger';

	/**
	 * Bootstrap icon class to display in header
	 */
	@Input() icon = 'bi-exclamation-triangle-fill';

	/**
	 * Whether to show the icon in the header
	 */
	@Input() showIcon = false;

	/**
	 * Whether the modal is in loading state (shows spinner, disables all buttons)
	 */
	@Input() loading = false;

	/**
	 * If true, the modal won't auto-close when confirm is clicked.
	 * The caller must manually close the modal using modalRef.close()
	 * This allows showing loading state during async operations.
	 */
	@Input() manualClose = false;

	/**
	 * Event emitted when user confirms in manual close mode
	 * Subscribe to this event when manualClose is true
	 */
	@Output() confirmed = new EventEmitter<void>();

	constructor(public activeModal: NgbActiveModal) {}

	/**
	 * User confirmed the action
	 */
	confirm(): void {
		if (this.manualClose) {
			// Don't close automatically - emit event instead
			this.confirmed.emit();
		} else {
			// Auto-close behavior (backward compatible)
			this.activeModal.close(true);
		}
	}

	/**
	 * User cancelled the action
	 */
	cancel(): void {
		this.activeModal.close(false);
	}
}
